import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { StockAlertI } from "@interfaces";
import { ValidateCurrentToken } from "../../helpers";

interface UseStockSocketProps {
  clientId: string;
  branchId: string | null;  // null = todas las sucursales
  role: string;
  onAlert?: (alert: StockAlertI) => void;
}

export const useStockSocket = ({
  clientId,
  branchId,
  role,
  onAlert,
}: UseStockSocketProps) => {
  // ▶ Map<string, StockAlertI>  — productId es string
  const socketRef = useRef<Socket | null>(null);
  const [alerts, setAlerts] = useState<Map<string, StockAlertI>>(new Map());

  useEffect(() => {
    // No conectar si no hay clientId válido
    if (!clientId) return;

    // el cliente y los permisos salen del JWT del handshake; "join" solo
    // pide la sucursal y el backend la acepta únicamente si es de su cliente.
    const token = ValidateCurrentToken();
    if (typeof token !== "string") return;

    socketRef.current = io(
      process.env.REACT_APP_URL_BACKEND ?? "http://localhost:2000",
      { transports: ["websocket"], auth: { token } },
    );

    socketRef.current.emit("join", { branchId });

    // stock bajo
    socketRef.current.on("low-stock", (payload: StockAlertI) => {
      setAlerts((prev) => {
        const next = new Map(prev);
        // ▶ payload.productId ya es string — no hay conversión
        next.set(payload.productId, { ...payload, isOutOfStock: false });
        return next;
      });
      onAlert?.(payload);
    });

    // sin stock
    socketRef.current.on("out-of-stock", (payload: StockAlertI) => {
      setAlerts((prev) => {
        const next = new Map(prev);
        next.set(payload.productId, { ...payload, isOutOfStock: true });
        return next;
      });
      onAlert?.(payload);
    });

    // stock recuperado — quita la alerta
    socketRef.current.on("stock-recovered", (payload: StockAlertI) => {
      setAlerts((prev) => {
        const next = new Map(prev);
        next.delete(payload.productId);
        return next;
      });
    });

    return () => {
      socketRef.current?.emit("leave", { branchId });
      socketRef.current?.disconnect();
    };
  }, [clientId, branchId, role]); // onAlert excluido intencionalmente — ver nota abajo

  // helpers — todos reciben string
  const getAlert = (productId: string) => alerts.get(productId) ?? null;
  const hasAlert = (productId: string) => alerts.has(productId);
  const totalAlerts = alerts.size;
  const outOfStockList = Array.from(alerts.values()).filter((a) => a.isOutOfStock);
  const lowStockList   = Array.from(alerts.values()).filter((a) => !a.isOutOfStock);

  return { alerts, getAlert, hasAlert, totalAlerts, outOfStockList, lowStockList };
};